import type { UseWhatsAppMessageCreateResponse } from '../../types/api/messages-mutations.types';
import { beginMessageMutationOrdering } from 'src/modules/shared/realtime/communications/utils/cache/event-order.util';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateWhatsAppMessage as useSharedWhatsAppMessage } from 'src/modules/shared/messaging/hooks/api/whatsapp-message-mutation';
import { refreshWhatsAppAvailability } from 'src/modules/shared/utils/integrations/refresh-whatsapp-availability.util';
import type { Message } from 'src/api/generated';
import { createOptimisticMessage } from 'src/modules/shared/messaging/utils/optimistic-whatsapp-message.util';
import type { WhatsAppMessageContext } from 'src/modules/shared/messaging/types/whatsapp-message-context.type';

const noopMessageSuccess = (
  _message: Message,
  _context: WhatsAppMessageContext
): void => undefined;
const noopMessageError = (
  _error: Error,
  _context: WhatsAppMessageContext
): void => undefined;

/**
 * Preserves the existing messaging consumer's cache and presentation lifecycle.
 * @param onSuccess - Reconciles an accepted message.
 * @param onError - Presents a failed send.
 * @param onUpdate - Projects the captured pending message.
 * @returns The existing consumer API backed by one shared mutation.
 */
export const useCreateWhatsAppMessage = (
  onSuccess: (
    message: Message,
    context: WhatsAppMessageContext
  ) => void = noopMessageSuccess,
  onError: (
    error: Error,
    context: WhatsAppMessageContext
  ) => void = noopMessageError,
  onUpdate?: (message: Message, context: WhatsAppMessageContext) => void
): UseWhatsAppMessageCreateResponse => {
  const queryClient = useQueryClient();

  return useSharedWhatsAppMessage({
    prepareSubmission: (params) => ({ params }),
    onMutate: async ({ params }) => {
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: ['crm/timeline-items/search'],
        }),
        queryClient.cancelQueries({
          queryKey: ['messaging/messages'],
        }),
      ]);

      const optimisticMessage = createOptimisticMessage(params);

      if (onUpdate) {
        onUpdate(optimisticMessage, { queryClient, params, optimisticMessage });
      }

      return {
        optimisticMessage,
        messageOrdering: beginMessageMutationOrdering(
          queryClient,
          optimisticMessage
        ),
      };
    },
    onSuccess: (message, { params }, context) => {
      void queryClient.invalidateQueries({
        queryKey: ['crm/funnel-channel-sessions'],
        exact: false,
      });

      try {
        onSuccess(message, {
          queryClient,
          params,
          optimisticMessage: context.optimisticMessage,
          messageOrdering: context.messageOrdering,
        });
      } finally {
        context.messageOrdering.dispose();
      }
    },
    onError: (error, { params }, context) => {
      refreshWhatsAppAvailability(queryClient);
      try {
        onError(error, {
          queryClient,
          params,
          optimisticMessage: context?.optimisticMessage,
        });
      } finally {
        context?.messageOrdering.dispose();
      }
    },
  });
};
